import * as fs from "node:fs/promises"
import path from "node:path"

type PackageJson = {
  browser?: string
  exports?: unknown
  "jsnext:main"?: string
  main?: string
  module?: string
  source?: string
  types?: string
  typings?: string
}

const packageEntryFieldPriority = ["source", "module", "jsnext:main", "browser", "main", "types", "typings"] as const
const packageDeclarationEntryFieldPriority = ["types", "typings", "source", "module", "jsnext:main", "browser", "main"] as const
const exportConditionPriority = [
  "source",
  "import",
  "module",
  "browser",
  "development",
  "production",
  "default",
  "require",
  "types",
  "typings",
] as const
const exportDeclarationConditionPriority = [
  "types",
  "typings",
  "source",
  "import",
  "module",
  "browser",
  "development",
  "production",
  "default",
  "require",
] as const
const sourceModuleFileExtensions = [".ts", ".tsx", ".mts", ".cts", ".mjs", ".js", ".jsx", ".cjs"]
const declarationModuleFileExtensions = [".d.ts", ".d.mts", ".d.cts"]
const indexEntryFileNames = [
  "index.ts",
  "index.tsx",
  "index.mts",
  "index.cts",
  "index.mjs",
  "index.js",
  "index.jsx",
  "index.cjs",
  "index.d.ts",
  "index.d.mts",
  "index.d.cts",
]
const declarationIndexEntryFileNames = [
  "index.d.ts",
  "index.d.mts",
  "index.d.cts",
  "index.ts",
  "index.tsx",
  "index.mts",
  "index.cts",
  "index.mjs",
  "index.js",
  "index.jsx",
  "index.cjs",
]

function getNodeModulesRoots(rootDir: string): string[] {
  const roots: string[] = []
  let current = path.resolve(rootDir)

  while (true) {
    roots.push(path.join(current, "node_modules"))
    const parent = path.dirname(current)
    if (parent === current) {
      break
    }

    current = parent
  }

  return roots
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function rootExportValue(exportsField: unknown): unknown {
  if (!isRecord(exportsField)) {
    return exportsField
  }

  if (Object.hasOwn(exportsField, ".")) {
    return exportsField["."]
  }

  if (Object.keys(exportsField).some(key => key.startsWith("."))) {
    return undefined
  }

  return exportsField
}

function exportTargetCandidates(
  value: unknown,
  conditionPriority: readonly string[] = exportConditionPriority,
): string[] {
  if (typeof value === "string") {
    return [value]
  }

  if (Array.isArray(value)) {
    return value.flatMap(item => exportTargetCandidates(item, conditionPriority))
  }

  if (!isRecord(value)) {
    return []
  }

  const orderedValues = [
    ...conditionPriority.map(condition => value[condition]),
    ...Object.entries(value)
      .filter(([condition]) => !conditionPriority.includes(condition))
      .map(([, target]) => target),
  ]

  return orderedValues.flatMap(item => exportTargetCandidates(item, conditionPriority))
}

async function packageJsonEntryPaths(packageDir: string, preferDeclarations = false): Promise<string[]> {
  const packageJsonPath = path.join(packageDir, "package.json")

  let packageJson: PackageJson
  try {
    packageJson = JSON.parse(await fs.readFile(packageJsonPath, "utf8")) as PackageJson
  } catch {
    return []
  }

  const exportEntries = exportTargetCandidates(
    rootExportValue(packageJson.exports),
    preferDeclarations ? exportDeclarationConditionPriority : exportConditionPriority,
  )
  const fieldEntries = (preferDeclarations ? packageDeclarationEntryFieldPriority : packageEntryFieldPriority)
    .map(field => packageJson[field])
    .filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0)

  return [...new Set([...exportEntries, ...fieldEntries])]
    .filter(entry => entry.startsWith(".") || !path.isAbsolute(entry))
    .map(entry => path.join(packageDir, entry))
}

async function resolveExistingEntryFile(entryPath: string, preferDeclarations = false): Promise<string | null> {
  try {
    const stats = await fs.stat(entryPath)
    if (stats.isFile()) {
      return entryPath
    }

    if (stats.isDirectory()) {
      for (const indexEntryFileName of preferDeclarations ? declarationIndexEntryFileNames : indexEntryFileNames) {
        const candidate = path.join(entryPath, indexEntryFileName)
        try {
          const candidateStats = await fs.stat(candidate)
          if (candidateStats.isFile()) {
            return candidate
          }
        } catch {
          continue
        }
      }
    }
  } catch {
    for (const extension of preferDeclarations ? declarationModuleFileExtensions : sourceModuleFileExtensions) {
      try {
        const candidate = `${entryPath}${extension}`
        const candidateStats = await fs.stat(candidate)
        if (candidateStats.isFile()) {
          return candidate
        }
      } catch {
        continue
      }
    }
  }

  return null
}

export async function getModuleEntryPath(rootDir: string, modulePath: string): Promise<string> {
  let entryFile: null | string = null

  const sourceFirstPossibleEntries = [
    ...indexEntryFileNames.map(entry => path.join(modulePath, entry)),
    path.join(modulePath, "dist/index.ts"),
    path.join(modulePath, "dist/index.tsx"),
    path.join(modulePath, "dist/index.mts"),
    path.join(modulePath, "dist/index.cts"),
    path.join(modulePath, "dist/index.mjs"),
    path.join(modulePath, "dist/index.js"),
    path.join(modulePath, "dist/index.jsx"),
    path.join(modulePath, "dist/index.cjs"),
    path.join(modulePath, "dist/index.d.ts"),
    path.join(modulePath, "dist/index.d.mts"),
    path.join(modulePath, "dist/index.d.cts"),
    path.join(modulePath, "lib/index.ts"),
    path.join(modulePath, "lib/index.tsx"),
    path.join(modulePath, "lib/index.mts"),
    path.join(modulePath, "lib/index.cts"),
    path.join(modulePath, "lib/index.mjs"),
    path.join(modulePath, "lib/index.js"),
    path.join(modulePath, "lib/index.jsx"),
    path.join(modulePath, "lib/index.cjs"),
    path.join(modulePath, "lib/index.d.ts"),
    path.join(modulePath, "lib/index.d.mts"),
    path.join(modulePath, "lib/index.d.cts"),
  ]
  const declarationFirstPossibleEntries = [
    ...declarationIndexEntryFileNames.map(entry => path.join(modulePath, entry)),
    path.join(modulePath, "dist/index.d.ts"),
    path.join(modulePath, "dist/index.d.mts"),
    path.join(modulePath, "dist/index.d.cts"),
    path.join(modulePath, "dist/index.ts"),
    path.join(modulePath, "dist/index.tsx"),
    path.join(modulePath, "dist/index.mts"),
    path.join(modulePath, "dist/index.cts"),
    path.join(modulePath, "dist/index.mjs"),
    path.join(modulePath, "dist/index.js"),
    path.join(modulePath, "dist/index.jsx"),
    path.join(modulePath, "dist/index.cjs"),
    path.join(modulePath, "lib/index.d.ts"),
    path.join(modulePath, "lib/index.d.mts"),
    path.join(modulePath, "lib/index.d.cts"),
    path.join(modulePath, "lib/index.ts"),
    path.join(modulePath, "lib/index.tsx"),
    path.join(modulePath, "lib/index.mts"),
    path.join(modulePath, "lib/index.cts"),
    path.join(modulePath, "lib/index.mjs"),
    path.join(modulePath, "lib/index.js"),
    path.join(modulePath, "lib/index.jsx"),
    path.join(modulePath, "lib/index.cjs"),
  ]

  const nodeModulesRoots = getNodeModulesRoots(rootDir)
  const nodeModulesPackageDirs = nodeModulesRoots.map(nodeModulesRoot => path.join(nodeModulesRoot, modulePath))
  const localPackageDir = path.join(rootDir, modulePath)
  const allPossibleEntriesPaths = [
    ...nodeModulesRoots.flatMap(nodeModulesRoot =>
      declarationFirstPossibleEntries.map(entry => path.join(nodeModulesRoot, entry)),
    ),
    path.join(rootDir, modulePath),
    ...sourceFirstPossibleEntries.map(entry => path.join(rootDir, entry)),
    ...(await Promise.all(nodeModulesPackageDirs.map(packageDir => packageJsonEntryPaths(packageDir, true)))).flat(),
    ...(await packageJsonEntryPaths(localPackageDir)),
  ]

  for (const entryPath of allPossibleEntriesPaths) {
    const resolvedEntryPath = await resolveExistingEntryFile(entryPath, entryPath.includes("/node_modules/"))
    if (resolvedEntryPath) {
      entryFile = resolvedEntryPath
      break
    }
  }

  if (!entryFile) {
    throw new Error(`Could not find entry file for "${modulePath}"`)
  }

  return entryFile
}
